import { t } from "@mmm/ui";
// Recently visited pages for the Home "Recent" section — pure client-side
// navigation history (a harmless local convenience per 08-ui-state.md), NOT
// domain data. Workspace layouts record a visit whenever their route is
// shown; Home reads the list and prunes entries whose subject no longer
// exists.

export interface RecentPage {
  /** Route to reopen (deep test routes are collapsed to their Tests tab). */
  path: string;
  /** Primary line — the subject or chapter title at visit time. */
  title: string;
  /** Muted context line — e.g. "Map", "Tests", "Biology · Map". */
  detail: string;
  color: string;
  subjectId: string;
  visitedAt: string;
}

const KEY = "mmm-recent-pages";
const MAX_ENTRIES = 8;

export function getRecentPages(): RecentPage[] {
  try {
    const raw = localStorage.getItem(KEY);
    const pages = raw ? (JSON.parse(raw) as RecentPage[]) : [];
    const seen = new Set<string>();
    const normalized: RecentPage[] = [];
    for (const page of pages) {
      const next = normalizeRecentPage(page);
      if (seen.has(next.path)) continue;
      seen.add(next.path);
      normalized.push(next);
    }
    return normalized;
  } catch {
    return [];
  }
}

export function recordRecentPage(entry: Omit<RecentPage, "visitedAt">): void {
  const rest = getRecentPages().filter((page) => page.path !== entry.path);
  const next = [{ ...entry, visitedAt: new Date().toISOString() }, ...rest].slice(0, MAX_ENTRIES);
  localStorage.setItem(KEY, JSON.stringify(next));
}

function normalizeRecentPage(page: RecentPage): RecentPage {
  const chapterMatch = page.path.match(/^\/subjects\/([^/]+)\/chapters\/([^/]+)(?:\/.*)?$/);
  if (chapterMatch) {
    return {
      ...page,
      path: `/subjects/${chapterMatch[1]}/chapters/${chapterMatch[2]}`,
      detail: page.detail.includes(" · ") ? page.detail.split(" · ")[0] : page.detail,
    };
  }

  const subjectMatch = page.path.match(/^\/subjects\/([^/]+)(?:\/.*)?$/);
  if (subjectMatch) {
    return {
      ...page,
      path: `/subjects/${subjectMatch[1]}`,
      // Translated at read time (getRecentPages runs on render), so this
      // label always follows the current UI language even though the rest of
      // the stored entry was written earlier.
      detail: t("common.subject"),
    };
  }

  return page;
}
